/**
 * MM SHOPPABLE VIDEO
 * ----------------------------------------------------------------------------------------------------------------
 * Three independent, small behaviors layered on top of a plain native horizontal-scroll row
 * (sections/mm-shoppable-video.liquid) — none of them replace the row's own swipe/scroll, they just add
 * controls on top of it:
 *   1. Play/pause button — toggles the section's own <video>, swaps its icon to reflect the CURRENT state
 *      (shows "pause" while playing, "play" while paused — the icon is the state, not the next action).
 *   2. Mute/unmute button — same pattern for video.muted.
 *   3. Prev/next slider arrows (desktop only, via CSS) — scrollBy() one card at a time, hidden entirely when
 *      there's nothing to scroll to (e.g. only 1-2 products fit already), kept in sync with the real scroll
 *      position via a scroll listener so they still work correctly if the visitor drags/swipes instead.
 */
(() => {
  const ICONS = {
    play: '<svg role="presentation" fill="none" focusable="false" width="12" height="12" viewBox="0 0 24 24"><path d="M5.77038 23.5048C5.43762 23.7187 5 23.4798 5 23.0842L5 0.915832C5 0.520249 5.43762 0.281329 5.77038 0.495243L23.0124 11.5794C23.3186 11.7762 23.3186 12.2238 23.0124 12.4206L5.77038 23.5048Z" fill="currentColor"></path></svg>',
    pause:
      '<svg role="presentation" fill="none" focusable="false" width="12" height="12" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"></rect><rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"></rect></svg>',
    mute:
      '<svg role="presentation" fill="none" focusable="false" width="16" height="16" viewBox="0 0 24 24"><path d="M3 9V15H7L13 21V3L7 9H3Z" fill="currentColor"></path><path d="M16 9L21 15M21 9L16 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>',
    unmute:
      '<svg role="presentation" fill="none" focusable="false" width="16" height="16" viewBox="0 0 24 24"><path d="M3 9V15H7L13 21V3L7 9H3Z" fill="currentColor"></path><path d="M16.5 8.5C17.5 9.5 18 10.7 18 12C18 13.3 17.5 14.5 16.5 15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"></path><path d="M19 6C20.8 7.8 21.5 9.8 21.5 12C21.5 14.2 20.8 16.2 19 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"></path></svg>',
  };

  function init(root) {
    const row = root.querySelector('[data-mm-shop-row]');
    const video = root.querySelector('video');
    const playButton = root.querySelector('[data-mm-shop-play]');
    const muteButton = root.querySelector('[data-mm-shop-mute]');
    const prev = root.querySelector('[data-mm-shop-prev]');
    const next = root.querySelector('[data-mm-shop-next]');
    // The progress bar is a sibling of .../__wrap (this `root`), not a child of it.
    const fill = root.parentElement && root.parentElement.querySelector('[data-mm-shop-progress-fill]');

    if (video && playButton) {
      const syncPlayIcon = () => {
        playButton.innerHTML = video.paused ? ICONS.play : ICONS.pause;
      };

      playButton.addEventListener('click', () => {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', syncPlayIcon);
      video.addEventListener('pause', syncPlayIcon);
      syncPlayIcon();
    }

    if (video && muteButton) {
      const syncMuteIcon = () => {
        muteButton.innerHTML = video.muted ? ICONS.mute : ICONS.unmute;
      };

      muteButton.addEventListener('click', () => {
        video.muted = !video.muted;
        syncMuteIcon();
      });

      syncMuteIcon();
    }

    if (!row || !prev || !next) return;

    const cards = Array.from(row.children);
    const count = cards.length;

    if (count < 2) return;

    // Real DOM positions, not "index * fixed card width" — with only a handful of cards, the last bit of
    // scrollable distance is rarely an exact multiple of one card's width (the browser clamps scrollLeft to
    // the real max, which can land short of "1 full step"), so dividing scrollLeft by a fixed step rounded
    // back down to index 0 right after clicking "next" — the prev arrow never came back. Matching against
    // each card's actual offsetLeft instead is correct regardless of how much room is left at either end.
    function currentIndex() {
      let closest = 0;
      let closestDistance = Infinity;

      cards.forEach((card, i) => {
        const distance = Math.abs(card.offsetLeft - row.scrollLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = i;
        }
      });

      return closest;
    }

    function update() {
      const maxScroll = row.scrollWidth - row.clientWidth;

      // Nothing to page through at all (every card already fits) — hide both arrows outright rather than
      // showing a "next" that would scroll 0px.
      if (maxScroll <= 1) {
        prev.hidden = true;
        next.hidden = true;
        if (fill) fill.style.width = '100%';
        return;
      }

      const index = currentIndex();
      prev.hidden = index === 0;
      next.hidden = index === count - 1;

      // Same "card (index + 1) of count" fill as sections/mm-media-row.liquid's progress bar — reads as
      // paging progress, not a literal scrollbar fraction.
      if (fill) fill.style.width = `${((index + 1) / count) * 100}%`;
    }

    function goToIndex(index) {
      const clamped = Math.min(count - 1, Math.max(0, index));
      row.scrollTo({ left: cards[clamped].offsetLeft, behavior: 'smooth' });
    }

    prev.addEventListener('click', () => goToIndex(currentIndex() - 1));
    next.addEventListener('click', () => goToIndex(currentIndex() + 1));

    let ticking = false;

    row.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;

        window.requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', update);

    update();
  }

  document.querySelectorAll('[data-mm-shop]').forEach(init);
})();
